import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, TextInput, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { Alert } from "@/src/lib/alert";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { ControlDisclosure, ControlId, ControlKeyValue } from "@/src/control/ControlDetails";
import { disableControlMember, listControlMembers, upsertControlMember } from "@/src/control/api";
import { useControlAccessContext } from "@/src/control/ControlAccessContext";
import { CONTROL_PERMISSIONS, CONTROL_ROLE_PERMISSIONS, hasControlPermission, type ControlMember, type ControlPermission, type ControlRole } from "@/src/control/permissions";
import { controlMemberStatusLabel, controlRoleLabel } from "@/src/control/presentation";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { action, border, radius, spacing, surface, text } from "@/src/theme/tokens";

const roles: ControlRole[] = ["viewer", "operator", "admin", "owner"];

export default function ControlMembersScreen() {
  return <ControlAccessGate permission="control.members.read"><Members /></ControlAccessGate>;
}

function Members() {
  const access = useControlAccessContext();
  const [members, setMembers] = useState<ControlMember[]>([]);
  const [uid, setUid] = useState("");
  const [role, setRole] = useState<ControlRole>("viewer");
  const [selectedPermissions, setSelectedPermissions] = useState<ControlPermission[]>([...CONTROL_ROLE_PERMISSIONS.viewer]);
  const [pendingSave, setPendingSave] = useState<{ uid: string; role: ControlRole; permissions: ControlPermission[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const canManage = access.status === "allowed" && hasControlPermission(access.member, "control.members.manage");
  const availableRoles = access.status === "allowed" && access.member.role === "owner" ? roles : roles.filter((item) => item !== "owner");
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    listControlMembers().then((value) => active && setMembers(value)).catch((cause: unknown) => active && setError(cause instanceof Error ? cause : new Error(String(cause)))).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt]);

  const effectiveSelection = CONTROL_PERMISSIONS.filter((permission) => selectedPermissions.includes(permission));

  function togglePermission(permission: ControlPermission) {
    setSelectedPermissions((current) => current.includes(permission)
      ? current.filter((item) => item !== permission)
      : [...current, permission]);
  }

  async function persistMember(pending: { uid: string; role: ControlRole; permissions: ControlPermission[] }) {
    setBusy(true);
    try {
      await upsertControlMember(pending);
      setUid("");
      setPendingSave(null);
      refresh();
    } catch (cause: unknown) {
      Alert.alert("Mutation refusée", friendlyFirebaseError(cause, "La mutation n’a pas pu être effectuée."));
    } finally { setBusy(false); }
  }

  function save() {
    if (!uid.trim() || busy) return;
    setPendingSave({ uid: uid.trim(), role, permissions: [...effectiveSelection] });
  }

  async function disable(member: ControlMember) {
    if (busy) return;
    Alert.alert("Désactiver ce membre ?", `${member.displayName || member.email || "Ce membre"} perdra immédiatement son accès.`, [
      { text: "Annuler", style: "cancel" },
      { text: "Désactiver", style: "destructive", onPress: async () => {
        setBusy(true);
        try { await disableControlMember(member.uid); refresh(); }
        catch (cause: unknown) { Alert.alert("Mutation refusée", friendlyFirebaseError(cause, "La mutation n’a pas pu être effectuée.")); }
        finally { setBusy(false); }
      } },
    ]);
  }

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.header}>
        <Text style={styles.kicker}>ACCÈS</Text>
        <Text style={styles.title}>Membres Control</Text>
        <Text style={styles.subtitle}>Gère les accès opérateurs. Les changements restent protégés et audités par le serveur.</Text>
      </View>

      {canManage ? <View style={styles.form}>
        <Text style={styles.formTitle}>Ajouter ou mettre à jour un membre</Text>
        <Text style={styles.helper}>L’identifiant Firebase est requis pour retrouver le compte à autoriser.</Text>
        <TextInput accessibilityLabel="Identifiant Firebase du membre" autoCapitalize="none" autoCorrect={false} onChangeText={setUid} placeholder="Identifiant Firebase" placeholderTextColor={text.muted} style={styles.input} value={uid} />
        <View style={styles.roles}>{availableRoles.map((item) => <Pressable accessibilityRole="button" accessibilityState={{ selected: item === role }} key={item} onPress={() => { setRole(item); setSelectedPermissions([...CONTROL_ROLE_PERMISSIONS[item]]); }} style={[styles.roleButton, item === role && styles.roleButtonActive]}><Text style={item === role ? styles.activeText : styles.body}>{controlRoleLabel(item)}</Text></Pressable>)}</View>
        <Text style={styles.helper}>Permissions effectives. Tu peux réduire le rôle à la liste minimale nécessaire.</Text>
        <View style={styles.permissions}>{CONTROL_PERMISSIONS.map((permission) => {
          const checked = selectedPermissions.includes(permission);
          return (
            <Pressable
              key={permission}
              accessibilityRole="checkbox"
              accessibilityState={{ checked }}
              onPress={() => togglePermission(permission)}
              style={styles.permissionRow}
            >
              <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
              </View>
              <Text style={styles.body}>{permission}</Text>
            </Pressable>
          );
        })}</View>
        <View style={styles.permissionPreview}>
          <Text style={styles.formTitle}>Avant validation</Text>
          <Text style={styles.body}>Rôle : {controlRoleLabel(role)}</Text>
          <Text style={styles.body}>Permissions : {effectiveSelection.length ? effectiveSelection.join(" · ") : "aucune"}</Text>
        </View>
        {pendingSave ? (
          <View style={styles.confirmation}>
            <Text style={styles.formTitle}>Confirmer l’accès Control</Text>
            <Text style={styles.body}>UID cible : {pendingSave.uid}</Text>
            <Text style={styles.body}>Rôle : {controlRoleLabel(pendingSave.role)}</Text>
            <Text style={styles.body}>Permissions effectives : {pendingSave.permissions.length ? pendingSave.permissions.join(" · ") : "aucune"}</Text>
            <View style={styles.confirmationActions}>
              <Pressable accessibilityRole="button" disabled={busy} onPress={() => void persistMember(pendingSave)} style={[styles.primary, busy && styles.disabled]}>
                <Text style={styles.primaryText}>{busy ? "Application…" : "Confirmer"}</Text>
              </Pressable>
              <Pressable accessibilityRole="button" disabled={busy} onPress={() => setPendingSave(null)} style={styles.cancelButton}>
                <Text style={styles.body}>Annuler</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <Pressable accessibilityRole="button" disabled={busy || !uid.trim()} onPress={save} style={[styles.primary, (busy || !uid.trim()) && styles.disabled]}><Text style={styles.primaryText}>Ajouter ou mettre à jour</Text></Pressable>
        )}
      </View> : null}

      {loading ? <ActivityIndicator color={text.brand} /> : error ? <Pressable accessibilityRole="button" onPress={refresh}><Text style={styles.error}>Chargement impossible. Réessayer.</Text></Pressable> : !members.length ? <Text style={styles.empty}>Aucun membre Control.</Text> : members.map((member) => (
        <View key={member.uid} style={styles.member}>
          <View style={styles.memberInfo}>
            <Text style={styles.memberName}>{member.displayName || "Membre Control"}</Text>
            {member.email ? <Text style={styles.email}>{member.email}</Text> : null}
            <View style={styles.badges}>
              <Text style={styles.roleBadge}>{controlRoleLabel(member.role)}</Text>
              <Text style={[styles.statusBadge, member.status === "disabled" && styles.badgeDisabled]}>{controlMemberStatusLabel(member.status)}</Text>
            </View>
          </View>
          <View style={styles.actions}>{canManage && member.status === "active" && access.status === "allowed" && member.uid !== access.member.uid ? <Pressable accessibilityRole="button" disabled={busy} onPress={() => void disable(member)}><Text style={styles.disable}>Désactiver</Text></Pressable> : null}</View>
          <ControlDisclosure>
            <ControlId label="UID Firebase" value={member.uid} />
            <ControlKeyValue label="Permissions détaillées" value={member.permissions.join(" · ")} />
          </ControlDisclosure>
        </View>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  header: { gap: spacing.sm },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 28, fontWeight: "800" },
  subtitle: { color: text.secondary, lineHeight: 22, minWidth: 0 },
  form: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md, padding: spacing.lg },
  formTitle: { color: text.primary, fontSize: 17, fontWeight: "800" },
  helper: { color: text.secondary, lineHeight: 20 },
  input: { borderColor: border.strong, borderRadius: radius.md, borderWidth: 1, color: text.primary, minHeight: 48, paddingHorizontal: spacing.md },
  roles: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  permissions: { borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.xs, padding: spacing.md },
  permissionRow: { alignItems: "center", flexDirection: "row", gap: spacing.sm, minHeight: 40 },
  checkbox: { alignItems: "center", borderColor: border.strong, borderRadius: 5, borderWidth: 1, height: 22, justifyContent: "center", width: 22 },
  checkboxChecked: { backgroundColor: surface.selected, borderColor: border.brand },
  checkboxMark: { color: text.brand, fontSize: 15, fontWeight: "800" },
  permissionPreview: { backgroundColor: surface.secondary, borderRadius: radius.md, gap: spacing.xs, padding: spacing.md },
  confirmation: { backgroundColor: surface.selected, borderColor: border.brand, borderRadius: radius.md, borderWidth: 1, gap: spacing.sm, padding: spacing.md },
  confirmationActions: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  cancelButton: { alignItems: "center", borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, justifyContent: "center", minHeight: 48, paddingHorizontal: spacing.lg },
  roleButton: { borderColor: border.subtle, borderRadius: radius.pill, borderWidth: 1, minHeight: 44, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  roleButtonActive: { backgroundColor: surface.selected, borderColor: border.brand },
  activeText: { color: text.brand, fontWeight: "700" },
  primary: { alignItems: "center", alignSelf: "flex-start", backgroundColor: action.primary, borderRadius: radius.md, minHeight: 48, justifyContent: "center", paddingHorizontal: spacing.lg, paddingVertical: spacing.md },
  primaryText: { color: text.inverse, fontWeight: "700" },
  disabled: { opacity: 0.5 },
  member: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  memberInfo: { gap: spacing.xs, minWidth: 0 },
  memberName: { color: text.primary, fontSize: 16, fontWeight: "700", minWidth: 0 },
  email: { color: text.secondary, minWidth: 0 },
  badges: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, paddingTop: spacing.xs },
  roleBadge: { backgroundColor: surface.selected, borderRadius: radius.pill, color: text.brand, fontWeight: "700", paddingHorizontal: spacing.md, paddingVertical: spacing.xs },
  statusBadge: { backgroundColor: surface.success, borderRadius: radius.pill, color: text.info, fontWeight: "700", paddingHorizontal: spacing.md, paddingVertical: spacing.xs },
  badgeDisabled: { backgroundColor: surface.danger, color: text.danger },
  actions: { alignItems: "flex-start", minHeight: 24 },
  disable: { color: text.danger, fontWeight: "700" },
  body: { color: text.secondary },
  empty: { color: text.secondary, paddingVertical: spacing.lg },
  error: { color: text.danger },
});
